import React, {
  type FC,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Flex, type UseDisclosureProps, useDisclosure } from '@chakra-ui/react';
import { type UseQueryResult } from '@tanstack/react-query';
import { type AxiosError } from 'axios';

import { Viewport } from '@/components/Viewport';
import { type CvAnalysisResponse } from '@/services/api';

import { CVAnalysisResultNavBar } from '../cv-analysis/CVAnalysisRootNavbar';
import { type InterviewLayoutContextNavDisplayed } from '../interview/InterviewLayout';
import { MediaDeviceProvider } from '../media/MediaDeviceContext';

type CVAnalysisResultContextValue = {
  navDisplayed: boolean;
  setNavDisplayed: React.Dispatch<React.SetStateAction<boolean>>;
  navDrawer: UseDisclosureProps;
  resultQuery?: UseQueryResult<
    CvAnalysisResponse,
    AxiosError<unknown, ExplicitAny>
  >;
};

export const CVAnalysisResultContext =
  React.createContext<CVAnalysisResultContextValue | null>(null);

export const useCVAnalysisResultContext = () => {
  const ctx = useContext(CVAnalysisResultContext);
  if (ctx === null) {
    throw new Error('Missing parent <CVAnalysisResultLayout> component');
  }
  return ctx;
};

export const useCVAnalysisHideNav = (displayed: boolean = true) => {
  const { setNavDisplayed } = useCVAnalysisResultContext();

  useEffect(() => {
    setNavDisplayed(displayed);
    return () => setNavDisplayed(true);
  }, [setNavDisplayed, displayed]);
};

export const CVAnalysisResultLayoutProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const navDrawer = useDisclosure();
  const [navDisplayed, setNavDisplayed] =
    useState<InterviewLayoutContextNavDisplayed>(true);

  const providerValue = useMemo(
    () => ({
      navDisplayed,
      setNavDisplayed,
      navDrawer,
    }),
    [navDisplayed, setNavDisplayed, navDrawer]
  );

  return (
    <CVAnalysisResultContext.Provider value={providerValue}>
      <Viewport bgGradient="linear(to-b, brand.25, gray.50)">
        {!!navDisplayed && <CVAnalysisResultNavBar display="block" />}
        <Flex flex="1" direction="column">
          <MediaDeviceProvider>{children}</MediaDeviceProvider>
        </Flex>
      </Viewport>
    </CVAnalysisResultContext.Provider>
  );
};
